import { createContext, ReactNode, useContext, useRef, useState } from 'react';
import { useMenuWithCart } from './MenuWithCart';
import styles from '../components/shared/notification/Notification.module.scss';

type NotificationOptions = {
  content: ReactNode;
  duration?: number;
};

type NotificationContextType = {
  showNotification: (options: NotificationOptions) => void;
  hideNotification: () => void;
};

interface NotificationProviderProps {
  children: ReactNode;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const NotificationProvider = ({ children }: NotificationProviderProps) => {
  const [notification, setNotification] = useState<ReactNode | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { isCartDrawerOpen } = useMenuWithCart();

  const hideNotification = () => {
    setNotification(null);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const showNotification = ({
    content,
    duration,
  }: NotificationOptions) => {
    setNotification(content);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    if (typeof duration === 'number') {
      timeoutRef.current = setTimeout(() => {
        hideNotification();
      }, duration);
    }
  };

  return (
    <NotificationContext.Provider value={{
      showNotification,
      hideNotification,
    }}
    >
      {children}
      {notification && !isCartDrawerOpen && (
        <div className={styles.notification_container}>{notification}</div>
      )}
    </NotificationContext.Provider>
  );
};

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) throw new Error('useNotification must be used within a NotificationProvider');
  return context;
};
